import { externalApiClient } from '@/shared/lib/apiClient';
import axios from 'axios';

export interface OrderMetricsPeriod {
  date: string;
  periodType: 'NIGHT' | 'BUSINESS';
  periodLabel: string;
  receivedCount: number;
  closedLostCount: number;
  requoteCount: number;
  unqualifiedCount: number;
  pendingCount: number;
  frt: { averageSeconds: number; medianSeconds: number } | null;
}

export interface OrderMetricsResponse {
  periods: OrderMetricsPeriod[];
}

export interface GetOrderMetricsParams {
  date?: string;
  startDate?: string;
  endDate?: string;
}

export interface MatchMetric {
  count: number;
  rate: number;
}

export interface VisitScheduleMatchPeriod {
  startDate: string;
  endDate: string;
  total: number;
  good: MatchMetric;
  mid: MatchMetric;
  bad: MatchMetric;
  worst: MatchMetric;
}

export interface VisitScheduleMatchResponse {
  periods: VisitScheduleMatchPeriod[];
}

export async function getVisitScheduleMatch(
  periods: { startDate: string; endDate: string }[]
): Promise<VisitScheduleMatchResponse> {
  try {
    const response = await externalApiClient.post<VisitScheduleMatchResponse>(
      '/api/v1/dashboard/visit-schedule-match',
      { periods }
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error?.message ||
        error.message ||
        '배정 품질 지표를 불러오지 못했습니다.';
      throw new Error(errorMessage);
    }
    throw new Error('알 수 없는 오류가 발생했습니다.');
  }
}

export interface AssignmentClosedLostEntry {
  appointedCount: number;
  closedLostAfterAppointmentCount: number;
  closedLostRatePercent: number | null;
}

export interface AssignmentClosedLostResponse {
  auto: AssignmentClosedLostEntry;
  manual: AssignmentClosedLostEntry;
}

export async function getAssignmentClosedLost(
  params: GetOrderMetricsParams
): Promise<AssignmentClosedLostResponse> {
  const searchParams = new URLSearchParams();
  if (params.date) {
    searchParams.set('date', params.date);
  } else if (params.startDate && params.endDate) {
    searchParams.set('startDate', params.startDate);
    searchParams.set('endDate', params.endDate);
  } else {
    throw new Error('date 또는 startDate/endDate를 지정해야 합니다.');
  }

  const url = `/api/v1/dashboard/assignment-closed-lost?${searchParams.toString()}`;
  try {
    const response = await externalApiClient.get<AssignmentClosedLostResponse>(url);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error?.message ||
        error.message ||
        '배정 지표를 불러오지 못했습니다.';
      throw new Error(errorMessage);
    }
    throw new Error('알 수 없는 오류가 발생했습니다.');
  }
}

export async function getOrderMetrics(
  params: GetOrderMetricsParams
): Promise<OrderMetricsResponse> {
  const searchParams = new URLSearchParams();
  if (params.date) {
    searchParams.set('date', params.date);
  } else if (params.startDate && params.endDate) {
    searchParams.set('startDate', params.startDate);
    searchParams.set('endDate', params.endDate);
  } else {
    throw new Error('date 또는 startDate/endDate를 지정해야 합니다.');
  }

  const url = `/api/v1/dashboard/order-metrics?${searchParams.toString()}`;
  try {
    const response = await externalApiClient.get<OrderMetricsResponse>(url);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error?.message ||
        error.message ||
        '지표를 불러오지 못했습니다.';
      throw new Error(errorMessage);
    }
    throw new Error('알 수 없는 오류가 발생했습니다.');
  }
}
